import React, { useState, useEffect, useCallback } from 'react';
import { AuthContext } from './auth-context';
import axios from 'axios';
import { ADMIN_TOKEN_KEY } from '../constants/tokenKeys';

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const QR_BASE_URL = process.env.REACT_APP_QR_BASE_URL;


const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [showAadhaarModal, setShowAadhaarModal] = useState(false); 
  const [fcmToken, setFcmToken] = useState(() => {
    return window.fcmToken || null;
  });
  const [bankModal, setBankModal] = useState(false);

  const [cachedLocation, setCachedLocation] = useState(null);
  const [cachedCompany, setCachedCompany] = useState(null);
// set true once SupervisorView has resolved a fresh location for this session
const [locationSessionActive, setLocationSessionActive] = useState(false);

const cacheLocation = useCallback((loc) => {
    setCachedLocation(loc);
    setLocationSessionActive(true);
  }, []);

const cacheCompany = useCallback((company) => {
  setCachedCompany(company);
}, []);

const clearCachedLocation = useCallback(() => {
    setCachedLocation(null);
    setLocationSessionActive(false);
    setCachedCompany(null); // clear stale company on logout too
  }, []);

  // Listen for FCM token updates from React Native
  useEffect(() => {
    const handler = () => {
      if (window.fcmToken) {
        setFcmToken(window.fcmToken);
        console.log('FCM Token received from React Native:', window.fcmToken);
      }
    };
    
    window.addEventListener('fcm-token-ready', handler);
    
    // Also check if token is already available
    if (window.fcmToken) {
      setFcmToken(window.fcmToken);
    }
    
    return () => window.removeEventListener('fcm-token-ready', handler);
  }, []);

  // Additional check: Poll for token if not received after a delay
  useEffect(() => {
    if (!fcmToken) {
      const interval = setInterval(() => {
        if (window.fcmToken) {
          setFcmToken(window.fcmToken);
          console.log('FCM Token found via polling:', window.fcmToken);
          clearInterval(interval);
        }
      }, 500);
      
      // Clear interval after 10 seconds if token not found
      setTimeout(() => clearInterval(interval), 10000);
      
      return () => clearInterval(interval);
    }
  }, [fcmToken]);

  // Function to get the FCM token reliably
  const getFCMToken = () => {
    // First check state
    if (fcmToken) return fcmToken;
    
    // Then check window object
    if (window.fcmToken) {
      setFcmToken(window.fcmToken);
      return window.fcmToken;
    }
    
    return null;
  };
   
  const verifyTokenWithAuth = async (authToken) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      
      if(response.data?.error?.name === "TokenExpiredError"){
        localStorage.removeItem(ADMIN_TOKEN_KEY);
      setToken(null);
      setUser(null);
       }

      //console.log(response.data);
  
      setUpUser({
        name: response.data.user.name,
        id: response.data.user.id,
        phone: response.data.user.phone,
        isAuthenticated: true,
        aadharVerified: response.data?.user?.isAadhaarVerified,
        department: response.data.user.department,
        skills: response.data.user.skills,
        skillIds:response.data.user.skillIds,
        gender: response.data.user.gender,
        panCard: response.data.user.panNumber,
        preferredLocation: response.data.user.preferredLocation,
        preferredLocationId: response.data.user.preferredLocationId,
        selectedSlot: response.data.user.shifttime,
        profilePhotoUrl: response.data.user.profilePhotoUrl,
        selectedWeekendSlot : response.data.user.weekend_shift,
        dob : response.data?.user?.dob,
        isBlacklisted:response.data?.user?.verification?.blacklist,
        isAgreementSigned: response.data?.user?.isAgreementSigned,
        address:response.data.user.address,
        workerCode:response?.data?.user?.workerCode,
        relativeNumber:response.data.user?.familyDetails?.RelativePhone,
        lastcompanyName:response.data.user?.lastCompany,
        highestQualification:response.data.user?.highestQualification,
        relativeName:response.data.user?.familyDetails?.RelativeName,
        relativeType:response.data.user?.familyDetails?.RelativeType,
        temporaryAddress:response.data.user?.temporaryAddress,
        payoutFrequency:response.data.user?.payoutFrequency,
        // isAdmin:response?.data?.user?.userType,
              isAdmin:"Admins",


        
        currentStatus: response.data.user.status,
        isAdmin: response.data.user.role === "Supervisor" ||
         (response.data.user.role === "Admin" && response.data.user.featureAccess.includes("Mark Attendance"))
      });
    
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setToken(null);
      setUser(null);
      throw error;
    }
  };

  const setUpToken = (token) => {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  setToken(token);
}

  const setUpUser = (user) => { 
    setUser(user);
  } 

  //console.log(user);

  const verifytoken = async () => {
    if (!token) {
      throw new Error('No token available');
    }
    await verifyTokenWithAuth(token);
  };
  

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const storedToken = localStorage.getItem(ADMIN_TOKEN_KEY);

        
        if (storedToken) {
          setToken(storedToken);
          await verifyTokenWithAuth(storedToken);
        }
      } catch (error) {
        console.error('Auth verification failed:', error);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };
  
    verifyAuth();
  }, []);

  useEffect(() => {
    // Initial check
    if (window.fcmToken) {
      setFcmToken(window.fcmToken);
    }
    
    const handler = () => {
      if (window.fcmToken) {
        setFcmToken(window.fcmToken);
        console.log('FCM Token updated:', window.fcmToken);
      }
    };
    
    window.addEventListener('fcm-token-ready', handler);
    
    // Also check periodically or on focus
    const interval = setInterval(() => {
      if (window.fcmToken && window.fcmToken !== fcmToken) {
        setFcmToken(window.fcmToken);
      }
    }, 1000);
    
    return () => {
      window.removeEventListener('fcm-token-ready', handler);
      clearInterval(interval);
    };
  }, [fcmToken]);

  //console.log("Token is",token);
  const InitiateLogin = async (phone, isWhatsapp) => {
    try {
      setLoading(true);
      
      // Get the FCM token using the helper function
      const appToken = getFCMToken();
      
      const payload = { 
        phone: "91" + phone, 
        isWhatsapp,
        ...(appToken && { appToken: appToken })
      };
      
      console.log('📱 InitiateLogin payload:', payload);
      console.log('🔑 FCM Token being sent:', appToken || 'No token available');
      
      if (!appToken) {
        console.warn('⚠️ No FCM token available. Push notifications may not work.');
      }
      
      const response = await axios.post(
        `${BASE_URL}/lenskart/initiate`,
        payload,
        { headers: { 'Content-Type': 'application/json' }}
      );
      return response.data.userId;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  //console.log("Token is",token);
  //console.log("User is",user);
  
 
  const VerifyOtp = async (userId, otp) => {
    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}/lenskart/verify-login-otp`, { userId, otp });
      // window.location.reload();
     // console.log(response.data);
      setUpToken(response.data.token);
      // setUpUser({
      //   name: response.data.user.name,
      //   id: response.data.user.id,
      //   phone: response.data.user.phone,
      //   skills:response.data.user.skills,
      //   preferredLocation: response.data.user.preferredLocation,
      //   isAuthenticated: true,
      //   aadharVerified: response.data.user.isAadhaarVerified,
      //   profilePhotoUrl: response.data.user.profilePhotoUrl,
      //   dob : response.data.user.dob,
      //   gender: response.data.user.gender,
      //   panCard: response.data.user.panNumber,
      //   address:response.data.user.address,
      //   isAgreementSigned:response.data.user.isAgreementSigned,
      //   department: response.data.user.department,
      //   selectedSlot: response.data.user.shifttime,
      //   selectedWeekendSlot: response.data.user.weekend_shift,
      //   currentStatus: response.data.user.status,
      //   isAdmin: response.data.user.userType === "Supervisor"
      // });
      await verifyTokenWithAuth(response.data.token);
    
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      console.log(token)
      const response = await axios.get(`${BASE_URL}/lenskart/factory`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
     
      console.log('dsdss',response.data);
      //console.log(token);
      return response.data;
    } catch (error) {
      console.error('Fetching job details failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const finalattendancesubmit = async (payload,selectedCompany,token) => {
    try {
      const response = await axios.post(`${BASE_URL}/lenskart-admin/confirm-bulk-mark-entry`, 
        {
        payload,
        selectedCompany
        },
        {
        headers: {
          Authorization: `Bearer ${token}`,
          
        }
      });
     
      console.log('dsdss',response.data);
      //console.log(token);
      return response.data;
    } catch (error) {
  console.error(
    "Upload failed:",
    error.response?.data || error.message
  );
  throw error;
}
 finally {
      setLoading(false);
    }
  }
















  const fetchAgreementUrl = async (name) => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${BASE_URL}/agreement/generate`,
      { name },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const publicUrl = response.data?.data?.publicUrl;
    if (!publicUrl) {
      console.error("Agreement generate response:", response.data);
      throw new Error(
        response.data?.message || response.data?.error || "Agreement URL missing in response"
      );
    }
    return publicUrl;
  } catch (error) {
    console.error("Fetching agreement URL failed:", error);
    throw error;
  } finally {
    setLoading(false);
  }
};

const generateAttendanceQR = (userId, currentDay, status) => {
  return `${QR_BASE_URL}/lkart/admin/attendance?userId=${userId}&currentDay=${currentDay}&status=${status}`;
};

  // const generateAttendanceQR = (userId, currentDay, status) => {
  //   return `https://jobs.factorykaam.com/lkart/admin/attendance?userId=${userId}&currentDay=${currentDay}&status=${status}`;
  // };
  
  const adminLogin = async (phone) => {
  try {
    setLoading(true);

    const appToken = getFCMToken();

    const payload = {
      phone: phone,
      ...(appToken && { appToken: appToken }),
    };

    console.log('📱 Admin Login payload:', payload);
    console.log('🔑 Admin FCM Token being sent:', appToken || 'No token available');

    // // ✅ SHOW POPUP WITH PAYLOAD
    // alert(
    //   'Admin Login Payload:\n\n' +
    //   JSON.stringify(payload, null, 2)
    // );

    if (!appToken) {
      console.warn('⚠️ No FCM token available for admin. Push notifications may not work.');
    }

    const response = await axios.post(
      `${BASE_URL}/lenskart-admin/signup`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Admin login failed:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  const verifyAdminOtp = async (phone, otp) => {
  try {
    setLoading(true);

    // ✅ Attach FCM token as a fallback in case adminLogin missed it
    const appToken = getFCMToken();

    const payload = {
      phone: phone,
      otp,
      ...(appToken && { appToken }),
    };

    const response = await axios.post(`${BASE_URL}/lenskart-admin/login`, payload);
    const data = response.data;

    if (data.success) {
      setUpToken(data.token);
      localStorage.setItem('isTablet', data.isTablet ?? false);

      setUpUser({
        name: 'admin',
        id: 'random',
        phone: 'not required',
        isAuthenticated: true,
        aadharVerified: false,
        department: 'any',
        selectedSlot: 'none',
        currentStatus: 'none',
        isAdmin: true,
      });
    }

    return data;
  } catch (error) {
    console.error('Admin OTP verification failed:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};
 
  //console.log(token);
  const initiateAadhaarVerification = async (aadhaarNo) => {
    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}/lenskart/initiate-aadhaar-verification`,
        { aadhaarNo },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      return response.data.requestId;
    } catch (error) {
      console.error('Aadhaar verification initiation failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const verifyAadhaar = async (requestId, otp, aadhaarNo) => {
    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}/lenskart/verify-aadhaar-otp`,
        { requestId, otp, aadhaarNo },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      return response.data.isAadhaarVerified;
    } catch (error) {
      console.error('Aadhaar verification failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const applyForJob = async (shifttime, weekend_shift,  jobId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}/lenskart/apply`,
        { weekend_shift, shifttime, job_id: jobId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      console.loe.log(response);
      setUser((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          selectedSlot: response.data.shifttime || prev.selectedSlot,
          selectedWeekendSlot: response.data.weekend_shift || prev.selectedWeekendSlot,
          currentStatus: response.data.status || prev.currentStatus
        };
      });
    
      return response.data;
    } catch (error) {
      console.error('Job application failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const fetchQRLink = async () => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${BASE_URL}/lenskart/qr`,
      {},
      { headers: { Authorization: `Bearer ${token}` }}
    );

    const qrLink = generateAttendanceQR(
      response.data.data.userId,
      response.data.data.currentDay,
      response.data.data.status
    );

  
    return {
      link: qrLink,
      status: response.data.data.status 
    };
  } catch (error) {
    console.error('QR link fetch failed:', error);
    throw error;
  } finally {
    setLoading(false);
  }
}

  const logout = async () => {
  try {
    setLoading(true);
    setUser(null);
    setToken(null);
    clearCachedLocation();  
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

  const fetchDownloadLink = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/lenskart/download-link`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.link;
    } catch (error) {
      console.error('Download link fetch failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const MarkAttendance = async (userId, currentDay, status) => {
    try {
      setLoading(true);
    
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/mark-attendance`,
        { userId, date: currentDay, status },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      return response.data.data;
    } catch (error) {
      // Handle different types of errors
      if (error.response) {
        // Server responded with error (4xx, 5xx)
        console.error('API Error:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
        throw new Error(error.response.data.message || 'Server error occurred');
      } else if (error.request) {
       
        throw new Error('Network connection failed. Please check your internet connection.');
      } else {
        // Something else happened while setting up the request
        console.error('Request Error:', error.message);
        throw new Error('Failed to send request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

const getMessageByPhone = async (phone) => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${BASE_URL}/lenskart-admin/getMessageByPhone`,
      { phone },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.success) {
      setMessageCount(response.data.count || 0);
    }
    
    return response.data;
  } catch (error) {
    console.error('Failed to fetch messages:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

const submitMessageResponse = async (messageId, response) => {
  try {
    setLoading(true);
    const result = await axios.post(
      `${BASE_URL}/lenskart-admin/submit-response`,
      { messageId, response },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return result.data;
  } catch (error) {
    console.error('Failed to submit response:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};

const markMessageAsRead = async (messageId) => {
  try {
    setLoading(true);
    const result = await axios.post(
      `${BASE_URL}/lenskart-admin/read-notification`,
      { messageId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return result.data;
  } catch (error) {
    console.error('Failed to mark message as read:', error);
    throw error;
  } finally {
    setLoading(false);
  }
};


//department allocation 
 const fetchNearestCompany = useCallback(async ({ latitude, longitude }) => {
  const response = await axios.post(
    `${BASE_URL}/worker-allotment/nearest-company`,
    { latitude, longitude },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
}, [token]);

 const fetchDepartmentsBySkills = async (companyId) => {
  const response = await axios.post(
    `${BASE_URL}/lenskart/getDepartments/Skills`,
    { companyId },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

 const assignWorkers = async (payload) => {
  const response = await axios.post(
    `${BASE_URL}/worker-allotment/assign`,
    payload,
   { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

 const freezeDepartmentApi = async ({ companyId, records }) => {
  const response = await axios.post(
    `${BASE_URL}/worker-allotment/allot-department`,
    { companyId, records },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

  const fetchMonthlyPayout = async (workerId)=>{
    try{
        setLoading(true);
        const response = await axios.get(
            `${BASE_URL}/lenskart/worker-monthly-referral-payout/${workerId}`,
           { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    } catch(error){
        console.error("Failed to fetch monthly payout:", error);
        throw error;
    } finally {
        setLoading(false);
    }
  };


    const fetchMonthlyInsentive = async (workerId)=>{
    try{
        setLoading(true);
        const response = await axios.get(
            `${BASE_URL}/lenskart/worker-monthly-incentive-payout/${workerId}`,
           { headers: { Authorization: `Bearer ${token}` } }
        );
        return response.data;
    } catch(error){
        console.error('Failed to fetch monthly incentives:', error);
        throw error;
    } finally {
        setLoading(false);
    }
  }

    const updatePaymentStatus = async ({incentiveId,status}) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/update-incentive-payment-status`,
        { incentiveId,
          status },
         { headers: { Authorization: `Bearer ${token}` } }
      );
      return response;
    } catch (error) {
      console.error('Adding skill failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };


  const admin_worker_payout_frequency_history = async (workerId) => {
  try {
    setLoading(true);
    const response = await axios.post(
      `${BASE_URL}/lenskart-admin/worker-frequency-history`,
      { workerId },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return response.data;
  } catch (error) {
    console.error("Failed to fetch payout history:", error);
    return { success: false, data: { history: [] } };
  } finally {
    setLoading(false);
  }
};

 const updateWorkerDepartmentApi = async ({ attendanceId, departmentId, prevDepartmentId }) => {
  const response = await axios.post(
    `${BASE_URL}/worker-allotment/update-worker-department`,
    { attendanceId, departmentId, prevDepartmentId },
   { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

const [messageCount, setMessageCount] = useState(0);

// Add function to update message count
const updateMessageCount = (count) => {
  setMessageCount(count);
};
  const value = {
    user,
    loading,
    InitiateLogin,
    logout,
    VerifyOtp,
    fetchJobDetails,
    fetchAgreementUrl,
    initiateAadhaarVerification,
    verifyAadhaar,
    applyForJob,
    fetchQRLink,
    token,
    initialized,
    showAadhaarModal, 
    bankModal,
    setShowAadhaarModal, 
    setBankModal,
    setUser,
    verifytoken,
    MarkAttendance,
    adminLogin,
    verifyAdminOtp,
    fetchDownloadLink,
    verifyTokenWithAuth,
    finalattendancesubmit,
    fcmToken,
    getMessageByPhone,
  submitMessageResponse,
  markMessageAsRead,
  messageCount,
  updateMessageCount,

      // worker allotment endpoints
    fetchNearestCompany,
    fetchDepartmentsBySkills,
    assignWorkers,
    freezeDepartmentApi,
    updateWorkerDepartmentApi,
    fetchMonthlyPayout,
    fetchMonthlyInsentive,
    updatePaymentStatus,
    admin_worker_payout_frequency_history,
    cachedLocation,
    locationSessionActive,
    cacheLocation,
    clearCachedLocation,
    cachedCompany,
    cacheCompany,
  };

  // if (!initialized) {
  //   return <div>Loading...</div>;
  // }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;